"use client";

import React, { useState, useMemo, useEffect } from 'react';
import { useChatContext } from '@/contexts/ChatContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { RoomDto, UserType, SearchResultItem } from '@/types';
import { formatRelativeTime, avatarDefault, getRoomDisplayName, getMessagePreviewText } from '@/lib/utils';
import { Search, ChevronDown, MessageSquare } from 'lucide-react';
import styles from './LeftPanel.module.css';

type SortOption = 'newest' | 'aToZ' | 'zToA';

export default function LeftPanel() {
    const { rooms, selectedRoom, selectRoom, userType, markAsRead, searchMessages, conversations, connectionStatus, getRoomUnreadCount } = useChatContext();
    const { t } = useLanguage();

    // Check if we're still connecting (not yet loaded rooms)
    const isConnecting = connectionStatus === 'Connecting...' || connectionStatus === 'Disconnected';

    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState<SortOption>('newest');
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const [messageSearchResults, setMessageSearchResults] = useState<SearchResultItem[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(async () => {
            if (searchQuery.trim()) {
                setIsSearching(true);
                try {
                    const results = await searchMessages(searchQuery);
                    setMessageSearchResults(results);
                } catch (e) {
                    console.error("Search error", e);
                } finally {
                    setIsSearching(false);
                }
            } else {
                setMessageSearchResults([]);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery, searchMessages]);

    // Filter and sort rooms (conversations)
    const filteredRooms = useMemo(() => {
        let result = [...rooms];

        // Filter by search query (local conversation search)
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(room =>
                room.groupName.toLowerCase().includes(query) ||
                room.emails?.toLowerCase().includes(query)
            );
        }

        // Sort
        result.sort((a, b) => {
            switch (sortBy) {
                case 'newest':
                    return (b.conversation?.latestMsgSendTime || b.createTime) -
                        (a.conversation?.latestMsgSendTime || a.createTime);
                case 'aToZ':
                    return a.groupName.localeCompare(b.groupName);
                case 'zToA':
                    return b.groupName.localeCompare(a.groupName);
                default:
                    return 0;
            }
        });

        return result;
    }, [rooms, searchQuery, sortBy]);

    const handleSelectRoom = async (room: RoomDto) => {
        selectRoom(room);
        if (room.conversationID) {
            await markAsRead(room.conversationID);
        }
        // Optional: specific message selection handling could go here
    };

    const handleSelectMessageResult = async (resultItem: SearchResultItem) => {
        // Find the room for this conversation
        const room = rooms.find(r => r.conversationID === resultItem.conversationID);
        if (room) {
            handleSelectRoom(room);
        } else {
            // Fallback if room not in current list (maybe fetch it?)
            // For now, try to find in conversations list and construct room
            const conv = conversations.find(c => c.conversationID === resultItem.conversationID);
            if (conv) {
                // Construct minimal room
                const minimalRoom: RoomDto = {
                    groupID: conv.groupID || '',
                    groupName: conv.groupInfo?.groupName || conv.showName || '',
                    createTime: conv.latestMsgSendTime || 0,
                    conversationID: conv.conversationID,
                    conversation: conv,
                    lastMessage: undefined, // We don't have it easily
                    typing: false,
                    typingUserID: '',
                    members: [], // Need fetch
                    emails: ''
                };
                handleSelectRoom(minimalRoom);
            }
        }
    };

    const getSortLabel = () => {
        switch (sortBy) {
            case 'newest': return t.newest;
            case 'aToZ': return t.aToZ;
            case 'zToA': return t.zToA;
        }
    };

    return (
        <div className={styles.leftPanel}>
            {/* Header */}
            <div className={styles.panelHeader}>
                <div className={styles.panelTitleRow}>
                    <h2 className={styles.panelTitle}>{t.chats}</h2>
                    <span className={styles.roomCount}>{rooms.length}</span>
                </div>
            </div>

            {/* Search */}
            <div className={styles.searchContainer}>
                <Search size={16} className={styles.searchIcon} />
                <input
                    type="text"
                    placeholder={t.searchChats}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={styles.searchInput}
                />
            </div>

            {/* Sort (hide when searching if desired, or keep) */}
            {!searchQuery && (
                <div className={styles.filterBar}>
                    <div className={styles.sortDropdown}>
                        <span className={styles.sortLabel}>{t.sortBy}</span>
                        <button
                            className={styles.sortButton}
                            onClick={() => setShowSortDropdown(!showSortDropdown)}
                            aria-haspopup="listbox"
                            aria-expanded={showSortDropdown}
                        >
                            {getSortLabel()}
                            <ChevronDown size={14} />
                        </button>
                        {showSortDropdown && (
                            <div className={styles.sortOptions} role="listbox">
                                <button onClick={() => { setSortBy('newest'); setShowSortDropdown(false); }}>{t.newest}</button>
                                <button onClick={() => { setSortBy('aToZ'); setShowSortDropdown(false); }}>{t.aToZ}</button>
                                <button onClick={() => { setSortBy('zToA'); setShowSortDropdown(false); }}>{t.zToA}</button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Room List */}
            <div className={styles.roomList}>
                {searchQuery ? (
                    <>
                        {/* Local Filter Matches */}
                        {filteredRooms.length > 0 && (
                            <div className={styles.searchSection}>
                                <div className={styles.sectionHeader}>{t.conversations}</div>
                                {filteredRooms.map(room => (
                                    <RoomItem
                                        key={room.groupID}
                                        room={room}
                                        isSelected={selectedRoom?.groupID === room.groupID}
                                        onClick={() => handleSelectRoom(room)}
                                        showStatus={userType === UserType.Sale}
                                        unreadCount={getRoomUnreadCount(room)}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Search API Matches */}
                        {messageSearchResults.length > 0 && (
                            <div className={styles.searchSection}>
                                <div className={styles.sectionHeader}>{t.messages}</div>
                                {messageSearchResults.map(result => (
                                    <div
                                        key={result.conversationID}
                                        className={styles.searchResultItem}
                                        onClick={() => handleSelectMessageResult(result)}
                                    >
                                        <div className={styles.roomAvatar} style={{ background: '#e0e7ff', color: '#4338ca' }}>
                                            {/* Use faceURL or default */}
                                            {result.faceURL ? <img src={result.faceURL} alt="" /> : <MessageSquare size={16} />}
                                        </div>
                                        <div className={styles.roomInfo}>
                                            <div className={styles.roomName}>{result.title}</div>
                                            <div className={styles.messageCount}>{result.messageCount} {t.matchedMessages}</div>
                                            {/* Show first matched message preview */}
                                            {result.messageList[0] && (
                                                <div className={styles.roomPreview}>
                                                    {result.messageList[0].textElem?.content}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {filteredRooms.length === 0 && messageSearchResults.length === 0 && !isSearching && (
                            <div className={styles.emptyState}>
                                <MessageSquare size={32} />
                                <p>{t.noResultsFound}</p>
                            </div>
                        )}
                        {isSearching && (
                            <div className={styles.emptyState}>
                                <p>{t.searching}</p>
                            </div>
                        )}
                    </>
                ) : (
                    // Regular List
                    filteredRooms.length > 0 ? (
                        filteredRooms.map(room => (
                            <RoomItem
                                key={room.groupID}
                                room={room}
                                isSelected={selectedRoom?.groupID === room.groupID}
                                onClick={() => handleSelectRoom(room)}
                                showStatus={userType === UserType.Sale}
                                unreadCount={getRoomUnreadCount(room)}
                            />
                        ))
                    ) : (
                        <div className={styles.emptyState}>
                            <MessageSquare size={32} />
                            <p>{isConnecting ? t.loading : t.noConversationsFound}</p>
                        </div>
                    )
                )}
            </div>
        </div>
    );
}

// Room Item Component
interface RoomItemProps {
    room: RoomDto;
    isSelected: boolean;
    onClick: () => void;
    showStatus: boolean;
    unreadCount: number;
}

function RoomItem({ room, isSelected, onClick, showStatus, unreadCount }: RoomItemProps) {
    const { t } = useLanguage();
    const lastMessageTime = room.conversation?.latestMsgSendTime || room.createTime;

    // Parse last message for preview
    let lastMessagePreview = '';
    if (room.typing) {
        // Find the nickname of the typing user from members
        const typingMember = room.members?.find(m => m.userID === room.typingUserID);
        const typingName = typingMember?.nickname || room.typingUserID || 'Someone';
        lastMessagePreview = `${typingName} ${t.isTyping}`;
    } else if (room.lastMessage) {
        lastMessagePreview = getMessagePreviewText(room.lastMessage, {
            newMessage: '',
            customMessage: t.customMessage,
            sticker: t.replySticker,
            image: t.image,
            video: t.video,
            file: t.file,
            voice: t.voice,
            location: t.location,
            replyMessage: t.replyMessage,
            thisMessageWasDeleted: t.thisMessageWasDeleted,
        });
    }

    return (
        <div
            className={`${styles.roomItem} ${isSelected ? styles.selected : ''}`}
            onClick={onClick}
        >
            <div className={styles.roomAvatar} style={{ backgroundColor: room.bgColor }}>
                {room.symbol}
            </div>

            <div className={styles.roomInfo}>
                <div className={styles.roomName}>{getRoomDisplayName(room.groupName, room.customerNo)}</div>
                <div className={styles.roomEmail}>{room.emails}</div>
                <div className={`${styles.roomPreview} ${room.typing ? styles.typing : ''}`}>
                    {lastMessagePreview}
                </div>
            </div>

            <div className={styles.roomMeta}>
                <div className={styles.roomTime}>{formatRelativeTime(lastMessageTime, t)}</div>
                {showStatus && (
                    <div className={`${styles.roomStatus} ${room.introduction === 'open' ? styles.open : styles.closed}`}>
                        {room.introduction === 'open' ? t.open : t.closed}
                    </div>
                )}
                {unreadCount > 0 && (
                    <div className={styles.roomBadge}>{unreadCount}</div>
                )}
            </div>
        </div>
    );
}
